/**
 * Pure helpers for the Azure resource picker tree.
 *
 * `buildTree` groups discovered resources into a subscription -> kind ->
 * resource tree with a fixed kind ordering; `filterTree` narrows that tree by a
 * case-insensitive substring match on resource name, resource group, or
 * subscription name. Both are side-effect-free and unit-tested — the picker
 * component consumes them and owns UI concerns (auto-expand, selection).
 */
import type {
  AzureDiscoveredResource,
  AzureDiscoveryEngine,
  AzureResourceKind,
} from '../../../types/azureDiscovery';

export interface AzureTreeGroup {
  subscriptionId: string;
  subscriptionName: string;
  kinds: { kind: AzureResourceKind; label: string; resources: AzureDiscoveredResource[] }[];
}

/** Fixed display order of resource kinds inside every subscription group. */
const KIND_ORDER: AzureResourceKind[] = [
  'app-insights',
  'log-analytics',
  'adx-cluster',
  'sql-server',
  'sql-managed-instance',
  'pg-flexible',
  'mysql-flexible',
  'storage-account',
];

/** Human-readable label for each resource kind's group header. */
export const KIND_LABEL: Record<AzureResourceKind, string> = {
  'app-insights': 'Application Insights',
  'log-analytics': 'Log Analytics',
  'adx-cluster': 'Azure Data Explorer',
  'sql-server': 'Azure SQL',
  'sql-managed-instance': 'SQL Managed Instance',
  'pg-flexible': 'PostgreSQL Flexible Server',
  'mysql-flexible': 'MySQL Flexible Server',
  'storage-account': 'Azure Storage',
};

/**
 * Group resources by subscription, then by kind (in `KIND_ORDER`). When
 * `engineFilter` is set, resources whose engine differs are dropped, so the
 * Add-Connection surface only shows kinds relevant to the current form.
 * Subscriptions are ordered by name; resources within a kind by name.
 */
export function buildTree(
  resources: AzureDiscoveredResource[],
  engineFilter?: AzureDiscoveryEngine
): AzureTreeGroup[] {
  const scoped = engineFilter ? resources.filter((r) => r.engine === engineFilter) : resources;

  const bySubscription = new Map<string, AzureDiscoveredResource[]>();
  for (const resource of scoped) {
    const existing = bySubscription.get(resource.subscriptionId);
    if (existing) existing.push(resource);
    else bySubscription.set(resource.subscriptionId, [resource]);
  }

  const groups: AzureTreeGroup[] = [];
  for (const [subscriptionId, subResources] of bySubscription) {
    const kinds = KIND_ORDER.map((kind) => ({
      kind,
      label: KIND_LABEL[kind],
      resources: subResources
        .filter((r) => r.kind === kind)
        .sort((a, b) => a.name.localeCompare(b.name)),
    })).filter((entry) => entry.resources.length > 0);

    groups.push({
      subscriptionId,
      subscriptionName: subResources[0].subscriptionName,
      kinds,
    });
  }

  return groups.sort((a, b) => a.subscriptionName.localeCompare(b.subscriptionName));
}

/**
 * Narrow a built tree by a case-insensitive substring match on resource name,
 * resource group, or subscription name. A subscription-name hit keeps every
 * resource in that subscription (they all share the name). Empty/whitespace
 * search returns the tree unchanged; no matches returns `[]`.
 */
export function filterTree(groups: AzureTreeGroup[], search: string): AzureTreeGroup[] {
  const query = search.trim().toLowerCase();
  if (query === '') return groups;

  const result: AzureTreeGroup[] = [];
  for (const group of groups) {
    const kinds = group.kinds
      .map((entry) => ({
        ...entry,
        resources: entry.resources.filter(
          (r) =>
            r.name.toLowerCase().includes(query) ||
            r.resourceGroup.toLowerCase().includes(query) ||
            r.subscriptionName.toLowerCase().includes(query)
        ),
      }))
      .filter((entry) => entry.resources.length > 0);

    if (kinds.length > 0) result.push({ ...group, kinds });
  }
  return result;
}
